/**
 * Items Hooks
 * TanStack Query hooks for items CRUD operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api, type ApiResponse, type ApiListResponse } from '@/lib/api';

// ============== Types ==============

/**
 * Generic item type - represents any collection item
 */
export type Item = Record<string, unknown>;

/**
 * Primary key type
 */
export type PrimaryKey = string | number;

/**
 * Query parameters for fetching items
 */
export interface ItemsQuery {
    fields?: string[];
    filter?: Record<string, unknown>;
    sort?: string[];
    limit?: number;
    page?: number;
    offset?: number;
    search?: string;
    meta?: string[];
    deep?: Record<string, unknown>;
}

/**
 * Response metadata
 */
export interface ItemsMeta {
    total_count?: number;
    filter_count?: number;
}

// ============== Query Keys ==============

export const itemsKeys = {
    all: ['items'] as const,
    lists: () => [...itemsKeys.all, 'list'] as const,
    list: (collection: string, query?: ItemsQuery) =>
        [...itemsKeys.lists(), collection, query] as const,
    details: () => [...itemsKeys.all, 'detail'] as const,
    detail: (collection: string, id: PrimaryKey) =>
        [...itemsKeys.details(), collection, id] as const,
};

// ============== Query String Builder ==============

/**
 * Build query string from ItemsQuery
 * Uses bracket notation for arrays to match the reference API format
 * Example: fields[]=id&fields[]=name&sort[]=sort&filter[status][_neq]=archived
 */
function buildQueryString(query: ItemsQuery): string {
    const parts: string[] = [];

    // Fields - use bracket notation for each field
    if (query.fields && query.fields.length > 0) {
        for (const field of query.fields) {
            parts.push(`fields[]=${encodeURIComponent(field)}`);
        }
    }

    // Sort - use bracket notation for each sort field
    if (query.sort && query.sort.length > 0) {
        for (const sortField of query.sort) {
            parts.push(`sort[]=${encodeURIComponent(sortField)}`);
        }
    }

    // Filter - convert to nested bracket notation
    if (query.filter && Object.keys(query.filter).length > 0) {
        const filterParts = buildFilterParams(query.filter, 'filter');
        parts.push(...filterParts);
    }

    // Search
    if (query.search) {
        parts.push(`search=${encodeURIComponent(query.search)}`);
    }

    // Limit
    if (query.limit !== undefined) {
        parts.push(`limit=${query.limit}`);
    }

    // Page (reference uses page, not offset)
    if (query.page !== undefined) {
        parts.push(`page=${query.page}`);
    } else if (query.offset !== undefined && query.limit) {
        // Convert offset to page for compatibility
        const page = Math.floor(query.offset / query.limit) + 1;
        parts.push(`page=${page}`);
    }

    // Meta - use bracket notation
    if (query.meta && query.meta.length > 0) {
        for (const metaField of query.meta) {
            parts.push(`meta[]=${encodeURIComponent(metaField)}`);
        }
    }

    return parts.length > 0 ? `?${parts.join('&')}` : '';
}

/**
 * Build filter params with nested bracket notation using indexed arrays
 * Example: filter[_and][0][status][_neq]=archived
 * Example: filter[_and][0][_or][0][name][_contains]=test
 */
function buildFilterParams(filter: Record<string, unknown>, prefix: string): string[] {
    const parts: string[] = [];

    for (const [key, value] of Object.entries(filter)) {
        const paramKey = `${prefix}[${key}]`;

        if (value !== null && typeof value === 'object' && !Array.isArray(value)) {
            // Nested object - recurse
            parts.push(...buildFilterParams(value as Record<string, unknown>, paramKey));
        } else if (Array.isArray(value)) {
            // Array value - add each item WITH INDEX for proper nesting
            for (let i = 0; i < value.length; i++) {
                const item = value[i];
                if (typeof item === 'object' && item !== null) {
                    parts.push(...buildFilterParams(item as Record<string, unknown>, `${paramKey}[${i}]`));
                } else {
                    parts.push(`${paramKey}[${i}]=${encodeURIComponent(String(item ?? ''))}`);
                }
            }
        } else {
            // Primitive value
            parts.push(`${paramKey}=${encodeURIComponent(String(value ?? ''))}`);
        }
    }

    return parts;
}

// ============== Query Hooks ==============

/**
 * Options for useItems hook
 */
export interface UseItemsOptions {
    enabled?: boolean;
}

/**
 * Fetch items from a collection
 */
export function useItems(collection: string, query: ItemsQuery = {}, options: UseItemsOptions = {}) {
    const { enabled = true } = options;

    return useQuery({
        queryKey: itemsKeys.list(collection, query),
        queryFn: async () => {
            const queryString = buildQueryString(query);
            const response = await api.get<ApiListResponse<Item>>(`/items/${collection}${queryString}`);
            const data = response.data as any;
            const items = Array.isArray(data) ? data : data ? [data] : [];
            return {
                items,
                meta: response.meta || null,
            };
        },
        enabled: !!collection && enabled,
    });
}

/**
 * Fetch a single item
 */
export function useItem(collection: string, id: PrimaryKey, query: ItemsQuery = {}) {
    return useQuery({
        queryKey: itemsKeys.detail(collection, id),
        queryFn: async () => {
            const queryString = buildQueryString(query);
            // Singletons are fetched without an id segment
            const path = id === null || id === undefined || id === 'singleton' ? `/items/${collection}` : `/items/${collection}/${id}`;
            const response = await api.get<ApiResponse<Item>>(`${path}${queryString}`);
            return response.data || null;
        },
        enabled: !!collection && !!id,
    });
}

// ============== Mutation Hooks ==============

/**
 * Create a new item
 */
export function useCreateItem(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: Partial<Item>) => {
            const response = await api.post<ApiResponse<Item>>(`/items/${collection}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: itemsKeys.lists() });
        },
    });
}

/**
 * Create multiple items
 */
export function useCreateItems(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (data: Partial<Item>[]) => {
            const response = await api.post<ApiListResponse<Item>>(`/items/${collection}`, data);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: itemsKeys.lists() });
        },
    });
}

/**
 * Update a single item
 */
export function useUpdateItem(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ id, data }: { id: PrimaryKey; data: Partial<Item> }) => {
            const path = id === null || id === undefined || id === 'singleton' ? `/items/${collection}` : `/items/${collection}/${id}`;
            const response = await api.patch<ApiResponse<Item>>(path, data);
            return response.data;
        },
        onSuccess: (_, variables) => {
            queryClient.invalidateQueries({ queryKey: itemsKeys.lists() });
            queryClient.invalidateQueries({
                queryKey: itemsKeys.detail(collection, variables.id)
            });
        },
    });
}

/**
 * Update multiple items with same data
 */
export function useUpdateItems(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ keys, data }: { keys: PrimaryKey[]; data: Partial<Item> }) => {
            const response = await api.patch<ApiListResponse<Item>>(`/items/${collection}`, { keys, data });
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: itemsKeys.lists() });
        },
    });
}

/**
 * Batch update items (each with individual data)
 */
export function useBatchUpdateItems(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (items: Partial<Item>[]) => {
            const response = await api.patch<ApiListResponse<Item>>(`/items/${collection}`, items);
            return response.data;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: itemsKeys.lists() });
        },
    });
}

/**
 * Delete a single item
 */
export function useDeleteItem(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (id: PrimaryKey) => {
            await api.delete(`/items/${collection}/${id}`);
            return id;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: itemsKeys.lists() });
        },
    });
}

/**
 * Delete multiple items
 */
export function useDeleteItems(collection: string) {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (keys: PrimaryKey[]) => {
            // Pass array directly - api.delete will JSON.stringify it
            await api.delete(`/items/${collection}`, keys);
            return keys;
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: itemsKeys.lists() });
        },
    });
}
